import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Paper,
  Tooltip,
  Alert,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  Search,
  Visibility,
  Comment,
  Person,
  FilterList,
  EmojiEvents as EmojiEventsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { storyService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { 
  StoriesGridSkeleton, 
  LoadingSpinner
} from '../components/Loading/LoadingComponents';
import { useAsyncOperation } from '../hooks/useLoading';

const Stories = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [stories, setStories] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('recent');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStories, setTotalStories] = useState(0);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const fetchingRef = useRef(false);

  // Enhanced loading with phases
  const loading = useAsyncOperation('stories');

  const fetchStories = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const phases = searchParams.get('search') 
        ? ['searching', 'filtering', 'ranking'] 
        : ['initial', 'processing'];

      await loading.execute(
        async () => {
          const params = {
            limit: limit,
            offset: (page - 1) * limit
          };

          const search = searchParams.get('search');
          if (search) {
            params.search = search;
          }

          console.log('Stories page: About to call storyService.getStories with params:', params);
          console.log('storyService.getStories function:', storyService.getStories);
          
          const response = await storyService.getStories(params);
          
          console.log('Stories page: Received response:', response);
        console.log('Stories data:', response.data?.data?.stories);
        
        const storiesData = response.data?.data?.stories || response.data?.stories || [];
        const totalCount = response.data?.data?.total || response.data?.total || storiesData.length;
        
        setStories(storiesData);
        setTotalStories(totalCount);
        setTotalPages(Math.ceil(totalCount / limit));
        
        return response.data;
      },
      {
        phases,
        showProgress: searchParams.get('search') ? true : false,
        progressDuration: 2000,
        onError: (error) => {
          console.error('Error fetching stories:', error);
          setStories([]);
          setShowErrorSnackbar(true);
        }
      }
    );
    } finally {
      fetchingRef.current = false;
    }
  }, [searchParams, sortBy, page, limit]); // Removed 'loading' to prevent infinite loop

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setSearchParams({ search: searchTerm });
    } else {
      setSearchParams({});
    }
    setPage(1);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchParams({});
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Browse Stories
      </Typography>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search stories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={<FilterList sx={{ mr: 1 }} />}
              >
                <MenuItem value="recent">Most Recent</MenuItem>
                <MenuItem value="popular">Most Popular</MenuItem>
                <MenuItem value="comments">Most Commented</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={limit}
                label="Per Page"
                onChange={(e) => {
                  setLimit(e.target.value);
                  setPage(1); // Reset to first page
                }}
              >
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value={24}>24</MenuItem>
                <MenuItem value={48}>48</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                fullWidth
              >
                Search
              </Button>
              {searchParams.get('search') && (
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      {loading.isLoading ? (
        <Box sx={{ mt: 3 }}>
          {/* Show skeleton loading for first 2 seconds, then spinner */}
          {loading.loadingPhase === 'initial' || loading.loadingPhase === 'processing' ? (
            <StoriesGridSkeleton count={12} />
          ) : (
            <LoadingSpinner 
              message={loading.loadingMessage}
              size={50}
              showProgress={loading.progress > 0}
              progress={loading.progress}
            />
          )}
        </Box>
      ) : loading.error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert 
            severity="error" 
            sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={fetchStories}
              >
                <RefreshIcon />
              </IconButton>
            }
          >
            {loading.error}
          </Alert>
        </Box>
      ) : stories.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            {searchParams.get('search') 
              ? `Search results for "${searchParams.get('search')}"` 
              : 'All Stories'
            } ({totalStories} total stories)
          </Typography>

          <Grid container spacing={3}>
            {stories.map((story) => (
              <Grid item xs={12} md={6} lg={4} key={story.story_id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate(`/story/${story.story_id}`)}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 1 }}>
                        {story.title}
                      </Typography>
                      
                      {/* Event Icon after title */}
                      {story.event_id && story.event && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <EmojiEventsIcon fontSize="small" color="primary" />
                          <Typography variant="caption" color="primary">
                            {story.event.title}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.6 }}
                    >
                      {story.content?.split(' ').slice(0, 15).join(' ') + '...' || 'No content available'}
                    </Typography>

                    {story.tags && story.tags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {story.tags.slice(0, 3).map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    {/* Author info - bottom left */}
                    <Typography variant="caption" color="text.secondary">
                      by {story.author?.given_name || 'Anonymous'}
                    </Typography>
                    
                    {/* Views and comments - bottom right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Visibility fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {story.view_count || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Comment fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {story.comment_count || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalStories)} of {totalStories} stories
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {searchParams.get('search') 
              ? `No stories found for "${searchParams.get('search')}"` 
              : 'No stories available yet'
            }
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {searchParams.get('search') 
              ? 'Try searching with different keywords.' 
              : 'Be the first to share your story!'
            }
          </Typography>
          {searchParams.get('search') ? (
            <Button variant="outlined" onClick={handleClearSearch}>
              Clear Search
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => navigate('/create')}
            >
              Write Your First Story
            </Button>
          )}
        </Paper>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchStories}>
              Retry
            </Button>
          }
        >
          Failed to load stories. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Stories;
